// Códigos de descuento y tarjetas regalo (admin).
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Plus, Edit2, Trash2, Loader2, AlertCircle, X, History, Copy, Mail, Gift } from 'lucide-react';

import {
  listCodigos, createCodigo, updateCodigo, deleteCodigo, listUsosCodigo, enviarCodigoEmail,
  listClasesTipos, listBonosTipos, listEventosAdmin,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

const AMBITOS = ['clases', 'bonos', 'eventos'];
const VACIO = {
  tipo: 'descuento', codigo: '', descripcion: '', modo_valor: 'porcentaje', valor: '', saldo_euros: '',
  aplica_a: ['clases', 'bonos', 'eventos'], clase_tipo_ids: [], bono_tipo_ids: [], evento_ids: [],
  importe_minimo_euros: '', email_restringido: '', fecha_inicio: '', fecha_fin: '',
  usos_max_total: '', usos_max_por_persona: '', activo: true,
};
const dia = (iso) => (iso ? String(iso).slice(0, 10) : '');
const eur = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`;

function estado(c) {
  if (!c.activo) return 'inactivo';
  const ahora = Date.now();
  if (c.fecha_inicio && new Date(c.fecha_inicio) > ahora) return 'programado';
  if (c.fecha_fin && new Date(c.fecha_fin) < ahora) return 'caducado';
  if (c.usos_max_total && c.usos_actuales >= c.usos_max_total) return 'agotado';
  if (c.tipo === 'regalo' && Number(c.saldo_euros) <= 0) return 'agotado';
  return 'vigente';
}

// Resumen de ventas de tarjetas regalo (las creadas a mano desde admin no cuentan como venta).
function resumenRegalos(items) {
  const r = { part: { n: 0, v: 0, p: 0 }, emp: { n: 0, v: 0, p: 0 } };
  items.forEach((c) => {
    if (c.tipo !== 'regalo') return;
    const k = c.origen === 'empresa' ? r.emp : c.origen === 'compra_regalo' ? r.part : null;
    if (!k) return;
    k.n += 1; k.v += Number(c.saldo_inicial_euros || 0); k.p += Number(c.saldo_euros || 0);
  });
  const n = r.part.n + r.emp.n, v = r.part.v + r.emp.v, p = r.part.p + r.emp.p;
  return { ...r, n, v, p, c: v - p };
}

export default function AdminCodigos() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);
  const [usos, setUsos] = useState(null);
  const [enviar, setEnviar] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try { setItems((await listCodigos()).data || []); }
    catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const alternar = async (c) => { try { await updateCodigo(c.id, { activo: !c.activo }); cargar(); } catch (e) { alert(e.message); } };
  const borrar = async (c) => {
    if (!window.confirm(t('admin.codigos.confirmar_desactivar', { codigo: c.codigo }))) return;
    try { await deleteCodigo(c.id); cargar(); } catch (e) { alert(e.message); }
  };
  const verUsos = async (c) => {
    try { setUsos({ codigo: c, data: (await listUsosCodigo(c.id)).data || [] }); } catch (e) { alert(e.message); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.codigos.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.codigos.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditar(VACIO)}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.crear_nuevo')}
        </button>
      </header>

      {!loading && !error && (() => {
        const R = resumenRegalos(items);
        if (!R.n) return null;
        return (
          <section className="admin-kpis">
            <div className="kpi">
              <div className="kpi-icon"><Gift size={20} strokeWidth={1.5} /></div>
              <div className="kpi-num">{R.n}</div>
              <div className="kpi-label">{t('admin.codigos.resumen.vendidas')}</div>
              <div className="kpi-foot">{t('admin.codigos.resumen.part')}: {R.part.n} · {t('admin.codigos.resumen.emp')}: {R.emp.n}</div>
            </div>
            <div className="kpi">
              <div className="kpi-num">{eur(R.v)}</div>
              <div className="kpi-label">{t('admin.codigos.resumen.importe')}</div>
              <div className="kpi-foot">{t('admin.codigos.resumen.part')}: {eur(R.part.v)} · {t('admin.codigos.resumen.emp')}: {eur(R.emp.v)}</div>
            </div>
            <div className="kpi">
              <div className="kpi-num">{eur(R.p)}</div>
              <div className="kpi-label">{t('admin.codigos.resumen.pendiente')}</div>
              <div className="kpi-foot">{t('admin.codigos.resumen.pendiente_ayuda')}</div>
            </div>
            <div className="kpi">
              <div className="kpi-num">{eur(R.c)}</div>
              <div className="kpi-label">{t('admin.codigos.resumen.canjeado')}</div>
            </div>
          </section>
        );
      })()}

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}
      {!loading && !error && items.length === 0 && <p className="mw-vacio">{t('admin.codigos.vacio')}</p>}

      {!loading && !error && items.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.codigos.col.codigo')}</th>
              <th>{t('admin.codigos.col.valor')}</th>
              <th>{t('admin.codigos.col.aplica')}</th>
              <th>{t('admin.codigos.col.vigencia')}</th>
              <th>{t('admin.codigos.col.usos')}</th>
              <th>{t('admin.codigos.col.estado')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const est = estado(c);
              return (
                <tr key={c.id}>
                  <td>
                    <strong>{c.codigo}</strong>
                    <div className="mw-sub">
                      {t(`admin.codigos.tipo.${c.tipo}`)}
                      {c.nombre_destinatario ? ` · ${c.nombre_destinatario}` : ''}
                      {c.email_restringido ? ` · ${c.email_restringido}` : ''}
                      {c.descripcion ? ` · ${c.descripcion}` : ''}
                    </div>
                  </td>
                  <td>
                    {c.tipo === 'regalo'
                      ? `${eur(c.saldo_euros)} / ${eur(c.saldo_inicial_euros)}`
                      : c.modo_valor === 'porcentaje' ? `${Number(c.valor)} %` : eur(c.valor)}
                  </td>
                  <td>{(c.aplica_a || []).map((a) => t(`admin.codigos.ambito.${a}`)).join(', ')}</td>
                  <td>{dia(c.fecha_inicio) || '…'} → {dia(c.fecha_fin) || '…'}</td>
                  <td>{c.usos_actuales}{c.usos_max_total ? ` / ${c.usos_max_total}` : ''}</td>
                  <td>
                    <button type="button" className={'estado-pill mw-pill-btn' + (est === 'vigente' ? ' estado-pill--ok' : '')} onClick={() => alternar(c)}>
                      {t(`admin.codigos.estado.${est}`)}
                    </button>
                  </td>
                  <td className="admin-acciones">
                    <button type="button" title={t('admin.codigos.copiar')} onClick={() => navigator.clipboard?.writeText(c.codigo)}><Copy size={16} strokeWidth={1.5} /></button>
                    <button type="button" title={t('admin.codigos.enviar')} onClick={() => setEnviar(c)}><Mail size={16} strokeWidth={1.5} /></button>
                    <button type="button" title={t('admin.codigos.ver_usos')} onClick={() => verUsos(c)}><History size={16} strokeWidth={1.5} /></button>
                    <button type="button" onClick={() => setEditar({
                      ...VACIO, ...c,
                      valor: c.valor ?? '', saldo_euros: c.saldo_euros ?? '', importe_minimo_euros: c.importe_minimo_euros ?? '',
                      email_restringido: c.email_restringido || '', descripcion: c.descripcion || '',
                      fecha_inicio: dia(c.fecha_inicio), fecha_fin: dia(c.fecha_fin),
                      usos_max_total: c.usos_max_total ?? '', usos_max_por_persona: c.usos_max_por_persona ?? '',
                      clase_tipo_ids: c.clase_tipo_ids || [], bono_tipo_ids: c.bono_tipo_ids || [], evento_ids: c.evento_ids || [],
                    })} aria-label={t('admin.editar')}><Edit2 size={16} strokeWidth={1.5} /></button>
                    <button type="button" onClick={() => borrar(c)} aria-label={t('admin.borrar')}><Trash2 size={16} strokeWidth={1.5} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editar && <CodigoModal item={editar} onCerrar={() => setEditar(null)} onGuardado={() => { setEditar(null); cargar(); }} />}
      {enviar && <EnviarModal codigo={enviar} onCerrar={() => setEnviar(null)} />}
      {usos && <UsosModal data={usos} onCerrar={() => setUsos(null)} />}
    </div>
  );
}

function EnviarModal({ codigo, onCerrar }) {
  const { t, i18n } = useTranslation();
  const [f, setF] = useState({ email: codigo.email_restringido || '', nombre: '', ocasion: 'especial', mensaje: '', idioma: ['es', 'en', 'fr', 'ru'].includes(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'es' });
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setEnviando(true); setMsg(null);
    try {
      await enviarCodigoEmail(codigo.id, f);
      setMsg({ ok: true, texto: t('admin.codigos.enviar_ok', { email: f.email }) });
    } catch (err) {
      setMsg({ ok: false, texto: err instanceof ApiError && err.message ? err.message : t('admin.codigos.enviar_err') });
    } finally { setEnviando(false); }
  };
  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{t('admin.codigos.enviar_titulo', { codigo: codigo.codigo })}</h2>
        <form onSubmit={submit} className="admin-form-grid one-col">
          <div className="admin-campo">
            <label>{t('admin.codigos.enviar_email')}</label>
            <input type="email" required value={f.email} onChange={set('email')} />
          </div>
          <div className="admin-campo">
            <label>{t('admin.codigos.enviar_nombre')}</label>
            <input type="text" value={f.nombre} onChange={set('nombre')} maxLength={80} />
          </div>
          <div className="admin-campo">
            <label>{t('admin.codigos.enviar_ocasion')}</label>
            <select value={f.ocasion} onChange={set('ocasion')}>
              {['especial', 'cumpleanos', 'navidad', 'aniversario', 'gracias'].map((o) => <option key={o} value={o}>{t(`admin.codigos.oc.${o}`)}</option>)}
            </select>
          </div>
          <div className="admin-campo">
            <label>{t('admin.codigos.enviar_mensaje')}</label>
            <textarea rows={3} value={f.mensaje} onChange={set('mensaje')} maxLength={400} />
          </div>
          <div className="admin-campo">
            <label>{t('admin.codigos.enviar_idioma')}</label>
            <select value={f.idioma} onChange={set('idioma')}>
              <option value="es">Español</option><option value="en">English</option>
              <option value="fr">Français</option><option value="ru">Русский</option>
            </select>
          </div>
          {msg && <div className={msg.ok ? 'admin-msg-ok' : 'profesor-foto-error'}>{msg.texto}</div>}
          <div className="admin-form-actions">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cerrar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <><Loader2 size={14} className="spin" /> {t('admin.codigos.enviar_enviando')}</> : <><Mail size={14} /> {t('admin.codigos.enviar_btn')}</>}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function UsosModal({ data, onCerrar }) {
  const { t } = useTranslation();
  return createPortal(
    <div className="modal-backdrop" onClick={onCerrar}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{t('admin.codigos.usos_de', { codigo: data.codigo.codigo })}</h2>
        {data.data.length === 0 ? <p className="mw-vacio">{t('admin.codigos.sin_usos')}</p> : (
          <table className="admin-table">
            <thead><tr><th>{t('admin.codigos.col.fecha')}</th><th>{t('admin.codigos.col.email')}</th><th>{t('admin.codigos.col.aplica')}</th><th>{t('admin.codigos.col.descuento')}</th></tr></thead>
            <tbody>
              {data.data.map((u) => (
                <tr key={u.id}>
                  <td>{new Date(u.created_at).toLocaleString()}</td>
                  <td>{u.email || '—'}</td>
                  <td>{t(`admin.codigos.ambito.${u.ambito}`)}</td>
                  <td>{eur(u.importe_descuento_euros)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>,
    document.body
  );
}

function CodigoModal({ item, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(item);
  const [clases, setClases] = useState([]);
  const [bonos, setBonos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const editando = !!item.id;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const regalo = form.tipo === 'regalo';

  useEffect(() => {
    listClasesTipos().then((r) => setClases(r.data || [])).catch(() => {});
    listBonosTipos().then((r) => setBonos(r.data || [])).catch(() => {});
    listEventosAdmin().then((r) => setEventos(r.data || [])).catch(() => {});
  }, []);

  const toggleAmb = (a) => setForm((f) => ({
    ...f, aplica_a: f.aplica_a.includes(a) ? f.aplica_a.filter((x) => x !== a) : [...f.aplica_a, a],
  }));
  const multi = (k) => (e) => setForm((f) => ({ ...f, [k]: Array.from(e.target.selectedOptions).map((o) => o.value) }));

  const submit = async (e) => {
    e.preventDefault();
    setEnviando(true); setError(null);
    try {
      const num = (v) => (v === '' || v == null ? null : Number(String(v).replace(',', '.')));
      const payload = {
        descripcion: form.descripcion,
        aplica_a: form.aplica_a,
        clase_tipo_ids: form.clase_tipo_ids, bono_tipo_ids: form.bono_tipo_ids, evento_ids: form.evento_ids,
        importe_minimo_euros: num(form.importe_minimo_euros),
        email_restringido: form.email_restringido,
        fecha_inicio: form.fecha_inicio || null,
        fecha_fin: form.fecha_fin ? `${form.fecha_fin}T23:59:59` : null,
        usos_max_total: num(form.usos_max_total),
        usos_max_por_persona: num(form.usos_max_por_persona),
        activo: !!form.activo,
      };
      if (regalo) payload.saldo_euros = num(form.saldo_euros);
      else { payload.modo_valor = form.modo_valor; payload.valor = num(form.valor); }
      if (editando) await updateCodigo(item.id, payload);
      else await createCodigo({ ...payload, tipo: form.tipo, codigo: form.codigo });
      onGuardado();
    } catch (err) {
      setError(err instanceof ApiError ? (t('admin.codigos.errores.' + err.code, { defaultValue: '' }) || err.message) : t('admin.error_generico'));
    } finally { setEnviando(false); }
  };

  const bloqueSel = (k, label, lista, nombre) => (
    <div className="modal-campo full">
      <label>{label}</label>
      <select multiple size={4} value={form[k]} onChange={multi(k)}>
        {lista.map((x) => <option key={x.id} value={x.id}>{nombre(x)}</option>)}
      </select>
      <small className="mw-ayuda">{t('admin.codigos.f.restriccion_ayuda')}</small>
    </div>
  );

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{editando ? t('admin.editar') : t('admin.codigos.nuevo')}</h2>
        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <div className="modal-campo">
              <label>{t('admin.codigos.f.tipo')}</label>
              <select value={form.tipo} onChange={set('tipo')} disabled={editando}>
                <option value="descuento">{t('admin.codigos.tipo.descuento')}</option>
                <option value="regalo">{t('admin.codigos.tipo.regalo')}</option>
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.codigos.f.codigo')}</label>
              <input type="text" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                disabled={editando} maxLength={40} placeholder={t('admin.codigos.f.codigo_ph')} />
            </div>
            {regalo ? (
              <div className="modal-campo">
                <label>{t('admin.codigos.f.saldo')} (€)</label>
                <input type="number" step="0.01" min="0" value={form.saldo_euros} onChange={set('saldo_euros')} required />
              </div>
            ) : (
              <>
                <div className="modal-campo">
                  <label>{t('admin.codigos.f.modo')}</label>
                  <select value={form.modo_valor} onChange={set('modo_valor')}>
                    <option value="porcentaje">{t('admin.codigos.f.modo_pct')}</option>
                    <option value="importe">{t('admin.codigos.f.modo_imp')}</option>
                  </select>
                </div>
                <div className="modal-campo">
                  <label>{t('admin.codigos.f.valor')} ({form.modo_valor === 'porcentaje' ? '%' : '€'})</label>
                  <input type="number" step="0.01" min="0" value={form.valor} onChange={set('valor')} required />
                </div>
              </>
            )}
            <div className="modal-campo full">
              <label>{t('admin.codigos.f.descripcion')}</label>
              <input type="text" value={form.descripcion} onChange={set('descripcion')} maxLength={200} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.codigos.f.aplica')}</label>
              <div className="codigo-checks">
                {AMBITOS.map((a) => (
                  <label key={a} className="mw-check"><input type="checkbox" checked={form.aplica_a.includes(a)} onChange={() => toggleAmb(a)} /> {t(`admin.codigos.ambito.${a}`)}</label>
                ))}
              </div>
            </div>
            {form.aplica_a.includes('clases') && bloqueSel('clase_tipo_ids', t('admin.codigos.f.solo_clases'), clases, (x) => x.nombre_es)}
            {form.aplica_a.includes('bonos') && bloqueSel('bono_tipo_ids', t('admin.codigos.f.solo_bonos'), bonos, (x) => x.nombre_es)}
            {form.aplica_a.includes('eventos') && bloqueSel('evento_ids', t('admin.codigos.f.solo_eventos'), eventos, (x) => x.nombre_es || x.slug)}
            <div className="modal-campo">
              <label>{t('admin.codigos.f.minimo')} (€)</label>
              <input type="number" step="0.01" min="0" value={form.importe_minimo_euros} onChange={set('importe_minimo_euros')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.codigos.f.email')}</label>
              <input type="email" value={form.email_restringido} onChange={set('email_restringido')} placeholder={t('admin.codigos.f.email_ph')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.codigos.f.desde')}</label>
              <input type="date" value={form.fecha_inicio} onChange={set('fecha_inicio')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.codigos.f.hasta')}</label>
              <input type="date" value={form.fecha_fin} onChange={set('fecha_fin')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.codigos.f.usos_total')}</label>
              <input type="number" min="1" step="1" value={form.usos_max_total} onChange={set('usos_max_total')} placeholder={t('admin.codigos.f.sin_limite')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.codigos.f.usos_persona')}</label>
              <input type="number" min="1" step="1" value={form.usos_max_por_persona} onChange={set('usos_max_por_persona')} placeholder={t('admin.codigos.f.sin_limite')} />
            </div>
            <div className="modal-campo full">
              <label className="mw-check"><input type="checkbox" checked={!!form.activo} onChange={set('activo')} /> {t('admin.codigos.f.activo')}</label>
              {regalo && !editando && <small className="mw-ayuda">{t('admin.codigos.f.regalo_ayuda')}</small>}
            </div>
          </div>
          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}
          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
